import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  IconButton,
  TooltipContent,
  TooltipRoot,
  TooltipTrigger,
} from '@socialchorus/shared-ui-components';
import { useTranslation } from 'react-i18next';
import styles from './ai-response-actions.module.scss';

interface AiResponseActionsProps {
  copy?: { html: string; text: string };
  isStreaming?: boolean;
  className?: string;
  pendoId?: string;
}

const COPIED_FEEDBACK_DURATION = 2000;

const AiResponseActions: React.FC<AiResponseActionsProps> = ({
  copy,
  isStreaming = false,
  className,
  pendoId,
}) => {
  const { t } = useTranslation();
  const [isCopied, setIsCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const onCopied = useCallback(() => {
    setIsCopied(true);
    timeoutRef.current = setTimeout(() => {
      setIsCopied(false);
    }, COPIED_FEEDBACK_DURATION);
  }, []);

  const handleCopy = useCallback(() => {
    if (copy?.html && typeof ClipboardItem !== 'undefined') {
      const htmlBlob = new Blob([copy.html], { type: 'text/html' });
      const textBlob = new Blob([copy.text], { type: 'text/plain' });
      const item = new ClipboardItem({
        'text/html': htmlBlob,
        'text/plain': textBlob,
      });
      navigator.clipboard
        .write([item])
        .then(onCopied)
        .catch(() => undefined);
    } else if (copy?.text) {
      navigator.clipboard
        .writeText(copy.text)
        .then(onCopied)
        .catch(() => undefined);
    }
  }, [copy, onCopied]);

  if (isStreaming) {
    return null;
  }

  return (
    <div
      className={`${styles.AiResponseActions} ${className || ''}`}
      {...(pendoId ? { 'data-pendo-id': pendoId } : {})}
    >
      <TooltipRoot>
        <TooltipTrigger asChild>
          <IconButton
            aria-label={isCopied ? t('common.copied') : t('common.copy')}
            iconName={isCopied ? 'check' : 'content_copy'}
            size="compact"
            onClick={handleCopy}
            className={`${styles.ActionButton} ${
              isCopied ? styles.ActionButtonCopied : ''
            }`}
            {...(pendoId ? { 'data-pendo-id': `${pendoId}-copy-button` } : {})}
          />
        </TooltipTrigger>
        <TooltipContent
          description={isCopied ? t('common.copied') : t('common.copy')}
        />
      </TooltipRoot>
    </div>
  );
};

export default AiResponseActions;
